import React, { useCallback, useState } from 'react'

import dayjs from 'dayjs'
import { UpdateSystemMessage, UpdateSystemMessageInput } from 'types/graphql'

import {
  DatetimeLocalField,
  FieldError,
  Form,
  Label,
  SubmitHandler,
  TextAreaField,
  TextField,
  useForm,
} from '@cedarjs/forms'

import Button from 'src/components/Custom/Button/Button'
import { SystemMessageType } from 'src/pages/SystemMessagingPage/SystemMessagesSection'

import HeadingLevel from '../Heading/Heading'
import Toggle from '../Toggle/Toggle'

import CustomModal from './CustomModal'

export type SystemMessageFormType = NonNullable<
  UpdateSystemMessage['updateSystemMessage']
>

interface ConfigureSystemMessageModalProps {
  header: string
  messageData?: SystemMessageType
  showModal: boolean
  handleModalClose: () => void
  onSave: (
    data: UpdateSystemMessageInput,
    id?: SystemMessageFormType['id']
  ) => void
}

type FormValues = {
  title?: string
  body?: string
  startAt: string
  endAt?: string
}

const ConfigureSystemMessageModal = ({
  header,
  messageData,
  showModal,
  handleModalClose,
  onSave,
}: ConfigureSystemMessageModalProps) => {
  const startAt = messageData?.startAt || dayjs()
  const endAt = messageData?.endAt
  const [displayStartAt, setDisplayStartAt] = useState(
    dayjs(startAt).isAfter(dayjs())
  )
  const [displayEndAt, setDisplayEndAt] = useState(!!endAt)
  const form = useForm<FormValues>()

  const handleDateChange = (start?: string, end?: string) => {
    const startValue = start ?? form.getValues('startAt') ?? startAt
    const endValue = end ?? form.getValues('endAt')

    const startDate = dayjs(startValue)
    const endDate = dayjs(endValue)

    if (endDate.isBefore(startDate)) {
      form.setError('endAt', {
        type: 'valueAsDate',
        message:
          'Please select an end date that comes after the start date, or leave field blank',
      })
    } else {
      form.clearErrors('endAt')
    }
    if (start) {
      form.setValue('startAt', start)
    }
    if (end) {
      form.setValue('endAt', end)
    }

    form.trigger('endAt')
  }

  const handleSave: SubmitHandler<FormValues> = async (data) => {
    const { startAt, endAt, title, body } = data
    // Skip save if no changes made.
    if (!form.formState.isDirty) {
      handleModalClose()
      return
    }
    try {
      const formatDateString = (dateString: string) => {
        if (!dateString) return null
        const dateObject = new Date(dateString)
        return dateObject.toISOString()
      }

      const input = {
        startAt: formatDateString(startAt),
        endAt: formatDateString(endAt),
        title: title,
        body: body,
      }

      onSave(input, messageData?.id)
    } catch (error) {
      console.error('Save failed:', error)
    }
  }

  const closeModal = useCallback(() => {
    if (form.formState.isDirty) {
      if (
        window.confirm(
          'Are you sure you want to leave? You may lose unsaved changes.'
        )
      ) {
        handleModalClose()
      }
    } else {
      handleModalClose()
    }
  }, [form.formState.isDirty, handleModalClose])

  // Keep track of body text character limit.
  const [bodyText, setBodyText] = useState(messageData?.body || '')
  const bodyFieldMaxLength = 480
  const bodyTextCurrentLength = bodyText.length

  return (
    <CustomModal
      showModal={showModal}
      setShowModal={closeModal}
      modalWidth="max-w-xl relative"
    >
      <Form onSubmit={handleSave} formMethods={form}>
        <HeadingLevel level={1}>{header}</HeadingLevel>
        {/* TITLE */}
        <Label
          name="title"
          className="rw-label mb-2 block w-full font-medium text-gray-700"
          errorClassName="rw-label rw-label-error"
        >
          Header
        </Label>
        <TextField
          name="title"
          defaultValue={messageData?.title}
          errorClassName="rw-input rw-input-error"
          className="focus:shadow-outline w-full appearance-none rounded border border-gray-300 px-0 pl-2 leading-tight text-gray-900 focus:outline-none"
          validation={{ maxLength: bodyFieldMaxLength }}
        />

        {/* BODY */}
        <Label
          name="body"
          className="rw-label mb-2 block w-full font-medium text-gray-700"
          errorClassName="rw-label rw-label-error"
        >
          Body text
        </Label>
        <TextAreaField
          name="body"
          defaultValue={messageData?.body}
          onChange={(e) => setBodyText(e.target.value)}
          errorClassName="rw-input rw-input-error"
          className="focus:shadow-outline w-full appearance-none rounded border border-gray-300 px-0 pl-2 leading-tight text-gray-900 focus:outline-none"
          validation={{ maxLength: 480 }}
        />
        <div
          className={`mb-4 text-sm ${bodyTextCurrentLength > bodyFieldMaxLength ? `text-red-500` : `text-gray-500`}`}
        >{`${bodyTextCurrentLength}/${bodyFieldMaxLength} character limit`}</div>

        {/* START AT */}
        <div className="flex justify-between">
          <div>
            <div className="mb1 mt-4 font-medium text-gray-700">
              Schedule for later
            </div>
            <div className="mb-4 text-sm text-gray-500">
              Publish this message at a specific date and time
            </div>
          </div>
          <div className="flex items-center">
            <Toggle
              value={displayStartAt}
              onValueChange={(checked: boolean) => {
                setDisplayStartAt(checked)
                if (!checked) {
                  form.setValue('startAt', dayjs().format('YYYY-MM-DDTHH:mm'))
                  form.clearErrors('startAt')
                }
              }}
            />
          </div>
        </div>
        <div className={displayStartAt ? 'block' : 'hidden'}>
          <Label
            name="startAt"
            className="mb-2 block w-full font-medium text-gray-700"
            errorClassName="rw-label rw-label-error"
          >
            Start Date
          </Label>
          <DatetimeLocalField
            defaultValue={startAt && dayjs(startAt).format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => handleDateChange(e.target.value, null)}
            name="startAt"
            className="date-picker-input w-full rounded-md border border-gray-300 py-2"
            errorClassName="rw-input rw-input-error"
            validation={{ required: 'Start date is required' }}
          />
          <FieldError name="startAt" className="text-red-500" />
        </div>

        {/* END AT */}
        <div className="flex justify-between">
          <div>
            <div className="mb1 mt-4 font-medium text-gray-700">
              Set end date
            </div>
            <div className="mb-4 text-sm text-gray-500">
              Automatically unpublish this message at a specific date and time
            </div>
          </div>
          <div className="flex items-center">
            <Toggle
              value={displayEndAt}
              onValueChange={(checked: boolean) => {
                setDisplayEndAt(checked)
                if (!!form.getValues('endAt') && !checked) {
                  form.resetField('endAt')
                  form.clearErrors('endAt')
                }
              }}
            />
          </div>
        </div>

        <div className={displayEndAt ? 'block' : 'hidden'}>
          <Label
            name="endAt"
            className="mb-2 block w-full font-medium text-gray-700"
            errorClassName="rw-label rw-label-error"
          >
            End Date
          </Label>
          <DatetimeLocalField
            defaultValue={endAt && dayjs(endAt).format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => handleDateChange(null, e.target.value)}
            name="endAt"
            className="date-picker-input w-full rounded-md border border-gray-300 py-2"
            errorClassName="rw-input rw-input-error"
            validation={{
              validate: (value) => {
                if (!value) return true
                const startValue = form.getValues('startAt') || startAt
                if (!startValue) return true
                const startDate = dayjs(startValue)
                const endDate = dayjs(value)
                return (
                  !endDate.isBefore(startDate) ||
                  'Please select an end date that comes after the start date, or leave field blank'
                )
              },
            }}
          />
          <FieldError name="endAt" className="text-red-500" />
        </div>

        <div className="mt-6 flex w-full justify-end gap-4">
          <Button
            title="Cancel"
            customButtonClass={'bg-white hover:bg-transparent'}
            onClick={closeModal}
          />
          <Button
            title="Save"
            type="submit"
            backgroundColor={'bg-indigo-600 hover:bg-indigo-800'}
          />
        </div>
      </Form>
    </CustomModal>
  )
}

export default ConfigureSystemMessageModal
